import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Loader from 'components/_UI/Loader';
import ticketsApi from 'api/requests/tickets';
import { Column } from 'styled/alignment/Column';
import SalesPanel from '../../_sales/SalesPanel';
import PostSaleSection from './PostSaleSection';
import RestrictedSalesPanel from 'modules/dashboard/events/collaboration/RestrictedSalesPanel';
import useEventAccess from 'hooks/useEventAccess';
import { EVENT_PERMISSIONS } from 'helpers/eventCollaboration';

const COPY = {
    soldLabel: 'Ingressos vendidos',
    iconName: 'ticket',
    sectionTitle: 'Desempenho por ingresso',
    sectionSubtitle: 'Quanto cada tipo vendeu, arrecadou e ainda tem em estoque.',
    sortLabel: 'Ordenar ingressos',
    hiddenLabel: 'Ingresso oculto',
    emptyText: 'Nenhum ingresso cadastrado para este evento.',
    chartTitle: 'Ingressos vendidos por dia',
    chartSubtitle: 'Quantidade de ingressos vendidos em cada dia, desde o início das vendas.',
    chartLabel: 'Ingressos vendidos',
    chartEmptyText: 'Ainda não há vendas registradas para exibir no gráfico.',
};

export default function TicketDetail({ data }) {
    const { can } = useEventAccess();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getEvent(data);
    }, [data]);

    async function getEvent(slug) {
        setIsLoading(true);

        try {
            const { data: response } = await ticketsApi.getTicketForEvent(slug);
            setPayload(response);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    if (isLoading) {
        return (
            <ContentLoader>
                <Loader />
            </ContentLoader>
        );
    }

    /* O pós-venda lista nome e e-mail de quem comprou, então segue a permissão
       dos participantes, não a das vendas. */
    const postSale = can(EVENT_PERMISSIONS.PARTICIPANTS_VIEW) ? <PostSaleSection key={data} eventSlug={data} /> : null;

    // Colaborador sem `event.sales.view`: a API já nem manda os números.
    if (!can(EVENT_PERMISSIONS.SALES_VIEW)) {
        return (
            <>
                <RestrictedSalesPanel items={payload?.tickets || []} copy={COPY} />
                {postSale}
            </>
        );
    }

    const soldOnline = Number(payload?.tickets_sold_online) || 0;
    const soldOffline = Number(payload?.tickets_sold_offline) || 0;
    const revenueOnline = Number(payload?.tickets_revenue_online) || 0;
    const revenueOffline = Number(payload?.tickets_revenue_offline) || 0;

    /* Os cards do topo levam só o online. Venda no PDV não tem lote, então
       somá-la aqui fazia o card divergir da lista de desempenho logo abaixo:
       o produtor conferia lote a lote e sempre faltava dinheiro. O presencial
       e o total geral aparecem em bloco próprio dentro do SalesPanel. */
    return (
        <>
            <SalesPanel
                items={payload?.tickets || []}
                copy={COPY}
                eventSlug={data}
                totals={{
                    sold: soldOnline,
                    total: payload?.tickets_total,
                    revenueSold: revenueOnline,
                    revenuePotential: payload?.tickets_revenue_total,
                }}
                channels={{ soldOnline, soldOffline, revenueOnline, revenueOffline }}
                soldByDay={payload?.tickets_sold_by_day || []}
            />
            {postSale}
        </>
    );
}

const ContentLoader = styled(Column)`
    min-height: 320px;
    display: flex;
    justify-content: center;
    align-items: center;
`;
